import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, User, Mail, Lock, Eye, EyeOff, Save } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import UserMenu from '@/components/UserMenu';
import Footer from '@/components/Footer';
import axios from 'axios';
import API_BASE_URL from '@/utils/apiUrl';

const BACKEND_URL = API_BASE_URL;

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, token, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    email: user?.email || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const handleUpdateEmail = async (e) => {
    e.preventDefault();
    
    if (!formData.email.trim()) {
      toast.error('Įveskite el. pašto adresą');
      return;
    }
    
    if (formData.email === user?.email) {
      toast.info('El. paštas nepasikeitė');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.put(
        `${BACKEND_URL}/api/auth/update-email`,
        { email: formData.email.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success('El. paštas atnaujintas!');
        await refreshUser();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Klaida atnaujinant el. paštą');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    
    if (!formData.currentPassword || !formData.newPassword) {
      toast.error('Užpildykite visus slaptažodžio laukus');
      return;
    }
    
    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('Nauji slaptažodžiai nesutampa');
      return;
    }
    
    if (formData.newPassword.length < 6) {
      toast.error('Naujas slaptažodis turi būti bent 6 simbolių');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.put(
        `${BACKEND_URL}/api/auth/update-password`,
        { 
          currentPassword: formData.currentPassword,
          newPassword: formData.newPassword 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success('Slaptažodis atnaujintas!');
        setFormData(prev => ({
          ...prev,
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        }));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Klaida atnaujinant slaptažodį');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-950 via-violet-950/50 to-slate-950">
      {/* Header */}
      <header className="relative z-10 p-4 flex justify-between items-center">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)} 
          className="text-amber-400 hover:text-amber-300"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Grįžti
        </Button>
        <UserMenu />
      </header>

      <main className="flex-1 container mx-auto px-4 py-8 max-w-lg">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-500/20 mb-4">
            <User className="w-8 h-8 text-amber-400" />
          </div>
          <h1 className="font-display text-2xl text-primary">Profilio nustatymai</h1>
          <p className="text-muted-foreground text-sm mt-2">
            Atnaujinkite savo el. paštą ir slaptažodį
          </p>
        </div>

        {/* Current Info */}
        <Card className="card-mystical p-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-purple-500/20">
              <User className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Prisijungęs kaip:</p>
              <p className="text-foreground font-medium">{user?.firstName} {user?.lastName}</p>
              <p className="text-xs text-muted-foreground">{user?.email}</p>
            </div>
          </div>
        </Card>

        {/* Update Email */}
        <Card className="card-mystical p-6 mb-6">
          <h2 className="font-display text-lg text-amber-400 mb-4 flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Keisti el. paštą
          </h2>
          
          <form onSubmit={handleUpdateEmail} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-foreground mb-1 block text-sm">
                Naujas el. paštas
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="jusu@elpastas.lt"
                className="bg-input border-border"
              />
            </div>
            
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-semibold"
            >
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Saugoma...' : 'Išsaugoti el. paštą'}
            </Button>
          </form>
        </Card>

        {/* Update Password */}
        <Card className="card-mystical p-6">
          <h2 className="font-display text-lg text-purple-400 mb-4 flex items-center gap-2">
            <Lock className="w-5 h-5" />
            Keisti slaptažodį
          </h2>
          
          <form onSubmit={handleUpdatePassword} className="space-y-4">
            <div>
              <Label htmlFor="currentPassword" className="text-foreground mb-1 block text-sm">
                Dabartinis slaptažodis
              </Label>
              <div className="relative">
                <Input
                  id="currentPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.currentPassword}
                  onChange={(e) => setFormData({ ...formData, currentPassword: e.target.value })}
                  placeholder="••••••••"
                  className="bg-input border-border pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            <div>
              <Label htmlFor="newPassword" className="text-foreground mb-1 block text-sm">
                Naujas slaptažodis
              </Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showNewPassword ? 'text' : 'password'}
                  value={formData.newPassword}
                  onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                  placeholder="••••••••"
                  className="bg-input border-border pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            <div>
              <Label htmlFor="confirmPassword" className="text-foreground mb-1 block text-sm">
                Pakartokite naują slaptažodį
              </Label>
              <Input
                id="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                placeholder="••••••••"
                className="bg-input border-border"
              />
            </div>
            
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white font-semibold"
            >
              <Lock className="w-4 h-4 mr-2" />
              {loading ? 'Saugoma...' : 'Atnaujinti slaptažodį'}
            </Button>
          </form>
        </Card>
      </main>

      <Footer />
    </div>
  );
}
